<template>
  <div class="order-shipment-container" v-loading="loading">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>发货记录 - {{ orderInfo.orderTitle || orderInfo.orderId || '加载中...' }}</span>
      </div>
      <div class="header-actions">
        <el-button 
          v-if="isSupplier && canCreateShipment && !isReturnOrder" 
          v-permission="'orders:order-shipment:create'"
          type="primary" 
          icon="el-icon-plus" 
          @click="showCreateDialog"
        >
          新增发货
        </el-button>
        <!-- 暂时隐藏确认全部已收货按钮 -->
        <!-- <el-button 
          v-if="isSupplier && canConfirmAllReceived" 
          type="success" 
          icon="el-icon-check" 
          @click="confirmAllReceived"
          :loading="confirmingAll"
        >
          确认全部已收货
        </el-button> -->
        <el-button @click="goBack" icon="el-icon-back">返回</el-button>
      </div>
    </div>

    <!-- 订单基本信息 -->
    <div class="info-section">
      <h3 class="section-title">订单信息</h3>
      <el-row :gutter="20">
        <el-col :span="12">
          <div class="info-item">
            <label>订单标题：</label>
            <span>{{ orderInfo.orderTitle || '未设置' }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>订单编号：</label>
            <span>{{ orderInfo.orderId || orderInfo.id || '未生成' }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>订单状态：</label>
            <span :class="'status-text status-' + orderInfo.status">{{ orderInfo.status | formatStatus }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>订单类型：</label>
            <span :class="'order-type-text order-type-' + orderInfo.orderType">{{ getOrderTypeText(orderInfo.orderType) }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>创建时间：</label>
            <span>{{ formatDate(orderInfo.createTime) }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>创建人：</label>
            <span>{{ maskName(orderInfo.creatorName) || '--' }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>合同名称：</label>
            <span>{{ getContractNames() }}</span>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="info-item">
            <label>合同编号：</label>
            <span>{{ getContractNumbers() }}</span>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 单位信息 -->
    <div class="info-section">
      <h3 class="section-title">单位信息</h3>
      <el-row :gutter="20">
        <!-- 需求单位信息（左边） -->
        <el-col :span="12">
          <div class="unit-info-block demand-unit">
            <h4 class="unit-title">需求单位</h4>
            <div class="info-item">
              <label>单位名称：</label>
              <span>{{ getDemandUnits() }}</span>
            </div>
            <div class="info-item">
              <label>二级机构：</label>
              <span>{{ getSecondaryOrgNames() }}</span>
            </div>
            <div class="info-item">
              <label>联系人：</label>
              <span>{{ maskName(orderInfo.demandContactPerson || orderInfo.contactPerson) || '--' }}</span>
            </div>
            <div class="info-item">
              <label>联系电话：</label>
              <span>{{ maskPhone(orderInfo.demandContactPhone || orderInfo.contactPhone) || '--' }}</span>
            </div>
            <div class="info-item">
              <label>收货地址：</label>
              <span>{{ getDeliveryAddress() }}</span>
            </div>
          </div>
        </el-col>
        
        <!-- 供应商单位信息（右边） -->
        <el-col :span="12">
          <div class="unit-info-block supplier-unit">
            <h4 class="unit-title">供应商单位</h4>
            <div class="info-item">
              <label>单位名称：</label>
              <span>{{ orderInfo.supplierDetails && orderInfo.supplierDetails.unitName || orderInfo.supplierName || '--' }}</span>
            </div>
            <div class="info-item">
              <label>联系人：</label>
              <span>{{ maskName(orderInfo.contactPerson) || '--' }}</span>
            </div>
            <div class="info-item">
              <label>联系电话：</label>
              <span>{{ maskPhone(orderInfo.contactPhone) || '--' }}</span>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    
    <!-- 订单金额信息 -->
    <div class="info-section" v-if="orderMaterials && orderMaterials.length > 0">
      <div class="total-info">
        <span>合计数量: {{ formatQuantity(totalQuantity) }}</span>
        <span style="margin-left: 20px;">合计金额: {{ formatAmount(totalPrice) }} 元</span>
        <!-- 多合同订单时显示合同信息 -->
        <span v-if="orderInfo.contractDetails && orderInfo.contractDetails.length > 1" style="margin-left: 20px;">
          涉及合同: {{ orderInfo.contractDetails.length }} 个
        </span>
      </div>
    </div>

    <!-- 发货记录列表 -->
    <div class="info-section">
      <h3 class="section-title">发货记录</h3>
      
      
      <div v-if="shipmentList && shipmentList.length > 0">
        <el-table
          :data="shipmentList"
          border
          stripe
          style="width: 100%"
          v-loading="loading"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
        >
          <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
          <el-table-column prop="trackingNumber" label="物流单号" min-width="150"></el-table-column>
          <el-table-column prop="logisticsCompany" label="物流公司" min-width="120"></el-table-column>
          <el-table-column prop="logisticsStatusText" label="物流状态" width="100" align="center"></el-table-column>
          <el-table-column label="发货产品" width="100" align="center">
            <template slot-scope="scope">
              <el-button type="text" @click="viewShipmentDetails(scope.row)">
                查看详情
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="签收状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.isReceived" type="success">已签收</el-tag>
              <el-tag v-else type="warning">未签收</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="签收人" width="120" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.isReceived">{{ scope.row.receivedBy || '-' }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="签收时间" width="200" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.isReceived && scope.row.receivedTime">
                {{ scope.row.receivedTime | formatDateTime }}
                <el-button
                  v-if="isReceiver(scope.row)"
                  type="text"
                  size="mini"
                  @click="handleEditReceivedTime(scope.row)"
                  style="margin-left: 4px; padding: 0 4px;"
                >
                  修改
                </el-button>
              </span>
              <span v-else-if="scope.row.isReceived">-</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="360" align="center">
            <template slot-scope="scope">
              <el-button
                type="text"
                size="mini"
                icon="el-icon-paperclip"
                @click="openAttachmentDialog(scope.row)"
              >
                附件资料
              </el-button>
              
              <!-- 打印送货单按钮 -->
              <el-button
                v-permission="'orders:order-shipment:print-delivery-note'"
                type="text"
                size="mini"
                icon="el-icon-printer"
                @click="printDeliveryNote(scope.row)"
                style="color: #409EFF"
              >
                打印送货单
              </el-button>
              
              <!-- 供应商操作 -->
              <template v-if="isSupplier">
                <el-button
                  v-if="canEditShipment(scope.row) && !isReturnOrder"
                  v-permission="'orders:order-shipment:edit'"
                  type="text"
                  size="mini"
                  @click="editShipment(scope.row)"
                >
                  编辑
                </el-button>
                <el-button
                  v-if="canDeleteShipment(scope.row) && !isReturnOrder"
                  v-permission="'orders:order-shipment:delete'"
                  type="text"
                  size="mini"
                  @click="deleteShipment(scope.row)"
                  style="color: #f56c6c"
                >
                  删除
                </el-button>
                <el-button
                  v-if="scope.row.logisticsStatus === '3'"
                  type="text"
                  size="mini"
                  @click="updateLogisticsStatus(scope.row, '4')"
                >
                  标记已到达
                </el-button>
                <!-- 补录物流信息按钮 - 已签收的记录可以补录 -->
                <el-button
                  v-if="scope.row.isReceived"
                  type="text"
                  size="mini"
                  @click="supplementLogistics(scope.row)"
                  style="color: #409EFF"
                >
                  补录物流信息
                </el-button>
              </template>
              
              <!-- 需求方操作 -->
              <template v-else-if="isDemander">
                <el-button
                  v-if="scope.row.logisticsStatus === '1' && !scope.row.isReceived"
                  v-permission="'orders:order-shipment:confirm-receive'"
                  type="success"
                  size="mini"
                  @click="handleReceive(scope.row)"
                >
                  确认收货
                </el-button>
                <span v-else-if="scope.row.isReceived" class="received-text">已签收</span>
                <span v-else class="pending-text">待到达</span>
              </template>
              
              <!-- 其他用户只能查看状态 -->
              <template v-else>
                <span v-if="scope.row.isReceived" class="received-text">已签收</span>
                <span v-else class="pending-text">待到达</span>
              </template>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[10, 20, 30, 50]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
          ></el-pagination>
        </div>
      </div>
      
      <div v-else class="no-data">
        <el-empty description="暂无发货记录">
          <el-button v-if="isSupplier && canCreateShipment && !isReturnOrder" type="primary" @click="showCreateDialog">
            新增发货
          </el-button>
        </el-empty>
      </div>
    </div>

    <!-- 新增/编辑发货对话框 -->
    <el-dialog
      v-dialog-drag
      :title="dialogTitle"
      :visible.sync="shipmentDialogVisible"
      width="60%"
      top="3vh"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <div class="dialog-content">
        <!-- 发货方式选择 -->
        <div class="form-section">
          <h4 class="form-section-title">发货方式</h4>
          <el-radio-group v-model="shipmentMode" @change="handleShipmentModeChange">
            <el-radio label="withLogistics">有物流信息</el-radio>
            <el-radio label="withoutLogistics">无物流信息</el-radio>
          </el-radio-group>
        </div>

        <!-- 物流信息表单 -->
        <div class="form-section" v-show="shipmentMode === 'withLogistics'">
          <h4 class="form-section-title">物流信息</h4>
          <el-form
            ref="logisticsForm"
            :model="logisticsForm"
            :rules="logisticsRules"
            label-width="120px"
            label-position="left"
          >
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="物流单号" prop="trackingNumber">
                  <el-input
                    v-model="logisticsForm.trackingNumber"
                    placeholder="请输入物流单号"
                    maxlength="100"
                    show-word-limit
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="物流公司" prop="logisticsCompany">
                  <el-input
                    v-model="logisticsForm.logisticsCompany"
                    placeholder="请输入物流公司名称"
                    maxlength="100"
                    show-word-limit
                  />
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="发货时间" prop="shipTime">
                  <el-date-picker
                    v-model="logisticsForm.shipTime"
                    type="datetime"
                    placeholder="选择发货时间"
                    format="yyyy-MM-dd HH:mm:ss"
                    value-format="yyyy-MM-ddTHH:mm:ss"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="预计到达时间">
                  <el-date-picker
                    v-model="logisticsForm.estimatedArrivalTime"
                    type="datetime"
                    placeholder="选择预计到达时间"
                    format="yyyy-MM-dd HH:mm:ss"
                    value-format="yyyy-MM-ddTHH:mm:ss"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item label="备注">
                  <el-input
                    v-model="logisticsForm.remark"
                    type="textarea"
                    placeholder="请输入备注信息"
                    maxlength="500"
                    show-word-limit
                    :rows="3"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </div>

        <!-- 产品选择区域 - 补录模式下隐藏 -->
        <div class="form-section" v-show="!isSupplementMode">
          <h4 class="form-section-title">
            选择发货产品
            <span class="selected-count" v-if="selectedProducts.length > 0">
              （已选择 {{ selectedProducts.length }} 项）
            </span>
          </h4>
          
          <div class="product-selection-area">
            <div class="selection-controls">
              <span class="selection-info">
                共 {{ availableProducts.length }} 项可发货产品，请在表格中选择需要发货的产品
              </span>
            </div>
            
            <el-table
              ref="productTable"
              :data="availableProducts"
              border
              fit
              stripe
              highlight-current-row
              style="width: 100%"
              :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
              @selection-change="handleSelectionChange"
              max-height="400"
            >
              <el-table-column type="selection" width="55" :selectable="checkSelectable"></el-table-column>
              <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
              <el-table-column prop="productName" label="材料名称" width="150"></el-table-column>
              <el-table-column prop="specification" label="规格型号" width="150">
                <template slot-scope="scope">
                  <el-tooltip
                    v-if="scope.row.specification"
                    :content="scope.row.specification"
                    placement="top"
                    effect="dark"
                    popper-class="spec-tooltip"
                  >
                    <span>{{ truncateText(scope.row.specification, 20) }}</span>
                  </el-tooltip>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
              <el-table-column label="总数量" width="100" align="center">
                <template slot-scope="scope">
                  {{ scope.row.quantity }}
                </template>
              </el-table-column>
              <el-table-column label="已发数量" width="100" align="center">
                <template slot-scope="scope">
                  {{ scope.row.shippedQuantity || 0 }}
                </template>
              </el-table-column>
              <el-table-column label="剩余数量" width="100" align="center">
                <template slot-scope="scope">
                  <span :class="{ 'no-stock': scope.row.remainingQuantity <= 0 }">
                    {{ scope.row.remainingQuantity }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="发货数量" width="180" align="center">
                <template slot-scope="scope">
                  <el-input-number
                    v-model="scope.row.shipQuantity"
                    :min="0"
                    :max="getMaxShipQuantity(scope.row)"
                    :precision="4"
                    :step="0.0001"
                    size="small"
                    style="width: 150px"
                    @change="handleQuantityChange(scope.row)"
                    :disabled="!isProductSelected(scope.row)"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="remark" label="备注" min-width="120">
                <template slot-scope="scope">
                  {{ scope.row.remark || '-' }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleDialogClose">取消</el-button>
        <el-button
          type="primary"
          @click="handleSubmit"
          :loading="submitting"
          :disabled="!isSupplementMode && selectedProducts.length === 0"
        >
          {{ isSupplementMode ? '保存物流信息' : (isEditMode ? '更新发货' : '确认发货') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 发货产品清单对话框 -->
    <el-dialog
      v-dialog-drag
      title="发货产品清单"
      :visible.sync="detailDialogVisible"
      width="80%"
      :close-on-click-modal="false"
    >
      <div v-if="currentShipmentDetail">
        <div class="shipment-items-table">
          <el-table
            :data="currentShipmentDetail.shipmentItems || []"
            border
            stripe
            style="width: 100%"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
            empty-text="暂无发货产品"
          >
            <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
            <el-table-column prop="productName" label="产品名称" min-width="150"></el-table-column>
            <el-table-column prop="specification" label="规格型号" min-width="150">
              <template slot-scope="scope">
                <el-tooltip
                  v-if="scope.row.specification"
                  :content="scope.row.specification"
                  placement="top"
                  effect="dark"
                  popper-class="spec-tooltip"
                >
                  <span>{{ truncateText(scope.row.specification, 20) }}</span>
                </el-tooltip>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="materialRequirement" label="材质要求" min-width="120">
              <template slot-scope="scope">
                {{ scope.row.materialRequirement || '--' }}
              </template>
            </el-table-column>
            <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
            <el-table-column prop="shippedQuantity" label="发货数量" width="100" align="center">
              <template slot-scope="scope">
                {{ scope.row.shippedQuantity }}
              </template>
            </el-table-column>
            <!-- 固定价合同显示合同价 -->
            <el-table-column prop="taxInclusiveUnitPrice" label="合同价" width="120" align="center" v-if="!hasFloatingPriceContract">
              <template slot-scope="scope">
                {{ formatUnitPrice(scope.row.taxInclusiveUnitPrice) }}
              </template>
            </el-table-column>
            <!-- 变动价合同显示含税变动价 -->
            <el-table-column prop="taxInclusiveFloatingPrice" label="含税变动价(元)" width="120" align="center" v-if="hasFloatingPriceContract">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.taxInclusiveFloatingPrice) }}
              </template>
            </el-table-column>
            <el-table-column prop="taxInclusiveTotalAmount" label="合价（元）" width="120" align="center">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.taxInclusiveTotalAmount) }}
              </template>
            </el-table-column>
            <el-table-column prop="productSupplierName" label="供应商" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.productSupplierName || '--' }}
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" min-width="120">
              <template slot-scope="scope">
                {{ scope.row.remark || '--' }}
              </template>
            </el-table-column>
          </el-table>
          
          <!-- 统计信息 -->
          <div style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 4px;">
            <el-row :gutter="20">
              <el-col :span="6">
                <div class="summary-item">
                  <label>产品种类：</label>
                  <span class="highlight">{{ (currentShipmentDetail.shipmentItems || []).length }} 种</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="summary-item">
                  <label>总数量：</label>
                  <span class="highlight">{{ getTotalQuantity() }}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="summary-item">
                  <label>总金额：</label>
                  <span class="highlight total-amount">{{ getTotalAmount() }}</span>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="summary-item">
                  <label>发货日期：</label>
                  <span>{{ formatShipmentDate() }}</span>
                </div>
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 物流、发货、验收附件对话框 -->
    <el-dialog
      v-dialog-drag
      title="附件资料"
      :visible.sync="attachmentDialogVisible"
      width="760px"
      :close-on-click-modal="false"
    >
      <div v-if="currentAttachmentLogistics" class="attachment-dialog-content">
        <div class="attachment-record-title">
          物流单号：{{ currentAttachmentLogistics.trackingNumber || '--' }}
        </div>
        <el-tabs v-model="activeAttachmentTab">
          <el-tab-pane label="物流信息" name="LOGISTICS">
            <shipment-attachments
              v-if="activeAttachmentTab === 'LOGISTICS'"
              :logistics-id="currentAttachmentLogistics.id"
              category="LOGISTICS"
              category-label="物流订单信息"
              :can-upload="isSupplier"
            />
          </el-tab-pane>
          <el-tab-pane label="发货资料" name="SHIPPING">
            <shipment-attachments
              v-if="activeAttachmentTab === 'SHIPPING'"
              :logistics-id="currentAttachmentLogistics.id"
              category="SHIPPING"
              category-label="装车信息或发货清单"
              :can-upload="isSupplier"
            />
          </el-tab-pane>
          <el-tab-pane label="验收资料" name="ACCEPTANCE">
            <shipment-attachments
              v-if="activeAttachmentTab === 'ACCEPTANCE'"
              :logistics-id="currentAttachmentLogistics.id"
              category="ACCEPTANCE"
              category-label="车辆到场或堆码资料"
              :can-upload="isDemander"
            />
          </el-tab-pane>
        </el-tabs>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="attachmentDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 确认收货对话框 -->
    <el-dialog
      v-dialog-drag
      title="确认收货"
      :visible.sync="receiveDialogVisible"
      width="760px"
      :close-on-click-modal="false"
    >
      <el-form ref="receiveForm" :model="receiveForm" :rules="receiveRules" label-width="100px">
        <el-form-item label="签收人" prop="receivedBy">
          <el-input v-model="receiveForm.receivedBy" placeholder="请输入签收人姓名" />
        </el-form-item>
        <el-form-item label="签收人电话" prop="receivedPhone">
          <el-input v-model="receiveForm.receivedPhone" placeholder="请输入签收人电话" />
        </el-form-item>
        <el-form-item label="收货日期" prop="receivedTime">
          <el-date-picker
            v-model="receiveForm.receivedTime"
            type="datetime"
            placeholder="请选择收货日期"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div v-if="currentReceiveLogistics" class="receive-attachment-section">
        <h4 class="form-section-title">验收资料</h4>
        <shipment-attachments
          :logistics-id="currentReceiveLogistics.id"
          category="ACCEPTANCE"
          category-label="车辆到场或堆码资料"
          :can-upload="true"
        />
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="receiveDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmReceive" :loading="receiving">
          确认签收
        </el-button>
      </div>
    </el-dialog>

    <!-- 修改收货时间对话框（仅签收人可见，仅可修改收货时间） -->
    <el-dialog
      v-dialog-drag
      title="修改收货时间"
      :visible.sync="editReceivedTimeDialogVisible"
      width="400px"
      :close-on-click-modal="false"
    >
      <el-form ref="editReceivedTimeForm" :model="editReceivedTimeForm" :rules="editReceivedTimeRules" label-width="100px">
        <el-form-item label="收货时间" prop="receivedTime">
          <el-date-picker
            v-model="editReceivedTimeForm.receivedTime"
            type="datetime"
            placeholder="请选择收货时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editReceivedTimeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmEditReceivedTime" :loading="editingReceivedTime">
          保存
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get_order_detail, update_order_status } from '@/api/modules/orders.js'
import { 
  receive_logistics,
  update_logistics_status,
  delete_logistics,
  get_shipment_list_by_logistics_id,
  create_logistics_with_shipment,
  get_or_create_delivery_note,
  query_logistics
} from '@/api/modules/logistics.js'
import { query_users_pagination } from '@/api/modules/user.js'
import { getApiUrl } from '@/utils/env'
import { maskName as maskNameUtil } from '@/utils/mask.js'
import { installPlatformPrintFooter } from '@/utils/print-footer'
import ShipmentAttachments from '@/components/ShipmentAttachments'

export default {
  name: 'OrderShipment',
  components: {
    ShipmentAttachments
  },
  filters: {
    // 日期格式化过滤器
    formatDate(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('日期格式化错误:', error);
        return dateString;
      }
    },
    // 日期时间格式化过滤器（与formatDate相同）
    formatDateTime(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('日期格式化错误:', error);
        return dateString;
      }
    },
    // 状态格式化过滤器
    formatStatus(status) {
      const statusMap = {
        'ACTIVE': '待接受',
        'EXECUTING': '执行中',
        'SENDING': '已发货',
        'RECEIVING': '已收货',
        'COMPLETED': '已完成',
        'CANCELLED': '已取消',
        'REJECTED': '已拒绝'
      };
      return statusMap[status] || status;
    }
  },
  data() {
    return {
      orderId: null,
      loading: false,
      submitting: false,
      receiving: false,
      orderInfo: {},
      orderMaterials: [],
      currentUnitId: null,
      totalQuantity: 0,
      totalPrice: 0,
      
      // 发货记录列表
      shipmentList: [],
      // 分页信息
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      
      // 物流表单
      logisticsForm: {
        trackingNumber: '',
        logisticsCompany: '',
        shipTime: '',
        estimatedArrivalTime: '',
        remark: ''
      },
      logisticsRules: {
        trackingNumber: [
          { required: true, message: '请输入物流单号', trigger: 'blur' },
          { min: 1, max: 100, message: '物流单号长度在 1 到 100 个字符', trigger: 'blur' }
        ],
        logisticsCompany: [
          { required: true, message: '请输入物流公司名称', trigger: 'blur' },
          { min: 1, max: 100, message: '物流公司名称长度在 1 到 100 个字符', trigger: 'blur' }
        ],
        shipTime: [
          { required: true, message: '请选择发货时间', trigger: 'change' }
        ]
      },
      
      // 产品选择
      selectedProducts: [],
      
      // 对话框控制
      shipmentDialogVisible: false,
      detailDialogVisible: false,
      receiveDialogVisible: false,
      attachmentDialogVisible: false,
      currentAttachmentLogistics: null,
      activeAttachmentTab: 'LOGISTICS',
      
      // 编辑模式
      isEditMode: false,
      currentEditLogistics: null,
      currentEditProducts: [], // 当前编辑的发货产品列表
      
      // 补录模式
      isSupplementMode: false, // 是否为补录物流信息模式
      
      // 发货方式
      shipmentMode: 'withLogistics', // 'withLogistics' 有物流信息, 'withoutLogistics' 无物流信息
      
      // 发货详情
      currentShipmentDetail: null,
      
      // 收货对话框
      receiveForm: {
        receivedBy: '',
        receivedPhone: '',
        receivedTime: '' // 用户选择的收货日期
      },
      receiveRules: {},
      currentReceiveLogistics: null,
      
      // 确认全部已收货
      confirmingAll: false,
      
      // 修改收货时间对话框
      editReceivedTimeDialogVisible: false,
      editReceivedTimeForm: { receivedTime: '' },
      editReceivedTimeRules: {},
      currentEditReceivedLogistics: null,
      editingReceivedTime: false
    }
  },
  computed: {
    // 判断是否是供应商
    isSupplier() {
      return this.currentUnitId && 
             this.orderInfo.supplierId && 
             this.currentUnitId === this.orderInfo.supplierId;
    },
    
    // 判断是否是需求方
    isDemander() {
      if (!this.currentUnitId || !this.orderInfo) {
        return false;
      }
      
      // 条件1：当前用户单位是需求方单位
      let isDemandUnit = false;
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        this.orderInfo.contractDetails.forEach(contract => {
          if (contract.demandUnits && contract.demandUnits.length > 0) {
            contract.demandUnits.forEach(unit => {
              // 尝试多种比较方式
              if (unit.unitId === this.currentUnitId || 
                  unit.unitId == this.currentUnitId || 
                  String(unit.unitId) === String(this.currentUnitId) ||
                  Number(unit.unitId) === Number(this.currentUnitId) ||
                  unit.id === this.currentUnitId ||
                  unit.id == this.currentUnitId) {
                isDemandUnit = true;
              }
            });
          }
        });
      }
      
      // 条件2：用户关联的项目id是订单的二级机构项目
      let isProjectUser = false;
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        this.orderInfo.contractDetails.forEach(contract => {
          if (contract.projects && contract.projects.length > 0) {
            const userProjectIds = this.getCurrentUserProjectIds();
            contract.projects.forEach(project => {
              if (userProjectIds.includes(project.projectId)) {
                isProjectUser = true;
              }
            });
          }
        });
      }
      
      return isDemandUnit || isProjectUser;
    },
    
    // 是否可以创建发货（供应商且订单状态为执行中、已发货或已收货）
    canCreateShipment() {
      return this.isSupplier && ['EXECUTING', 'SENDING', 'RECEIVING'].includes(this.orderInfo.status);
    },
    
    // 是否可以确认全部已收货（供应商且订单状态为已发货且有发货记录）
    canConfirmAllReceived() {
      return this.isSupplier && 
             this.orderInfo.status === 'SENDING' && 
             this.shipmentList && 
             this.shipmentList.length > 0;
    },
    
    // 对话框标题
    dialogTitle() {
      if (this.isSupplementMode) {
        return '补录物流信息';
      }
      return this.isEditMode ? '编辑发货' : '新增发货';
    },
    
    // 可发货的产品（编辑模式下显示所有原发货产品，新增模式下只显示剩余数量大于0的）
    availableProducts() {
      if (this.isEditMode && this.currentEditProducts && this.currentEditProducts.length > 0) {
        // 编辑模式：显示原发货记录中的所有产品
        return this.orderMaterials.filter(item => 
          this.currentEditProducts.some(editProduct => editProduct.orderListId === item.id)
        );
      } else {
        // 新增模式：只显示剩余数量大于0的产品
        return this.orderMaterials.filter(item => item.remainingQuantity > 0);
      }
    },
    
    // 判断是否有变动价合同
    hasFloatingPriceContract() {
      if (!this.orderMaterials || this.orderMaterials.length === 0) {
        return false;
      }
      
      // 检查订单项中是否有变动价项目
      return this.orderMaterials.some(item => 
        item.pricingMethod === 'FLOATING' || 
        (item.taxInclusiveFloatingPrice && item.taxInclusiveFloatingPrice > 0)
      );
    },
    
    // 判断是否为退货订单
    isReturnOrder() {
      return this.orderInfo.orderType === '02' || this.orderInfo.orderType === 'RETURN';
    },
    
  },
  created() {
    // 设置修改收货时间表单验证规则
    this.editReceivedTimeRules = {
      receivedTime: [
        { required: true, message: '请选择收货时间', trigger: 'change' }
      ]
    };

    
    // 设置收货表单验证规则
    this.receiveRules = {
      receivedBy: [
        { required: true, message: '请输入签收人姓名', trigger: 'blur' },
        { validator: this.validateReceivedBy, trigger: 'blur' }
      ],
      receivedPhone: [
        { required: true, message: '请输入签收人电话', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' },
        { validator: this.validateReceivedPhone, trigger: 'blur' }
      ],
      receivedTime: [
        { required: true, message: '请选择收货日期', trigger: 'change' }
      ]
    };
    
    // 获取当前用户单位ID
    this.getCurrentUnitId();
    
    // 从路由参数获取订单ID
    this.orderId = this.$route.params.orderId;
    if (!this.orderId) {
      this.$message.error('缺少订单ID参数');
      this.goBack();
      return;
    }
    
    // 加载订单详情
    this.getOrderDetail();
    
    // 加载发货记录
    this.getShipmentList();
  },
  methods: {
    // 获取当前用户的单位ID
    getCurrentUnitId() {
      try {
        const store = this.$store || {};
        const state = store.state || {};
        
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          userInfo = state.user.userInfo;
        } else if (state.userInfo) {
          userInfo = state.userInfo;
        } else if (state.auth && state.auth.userInfo) {
          userInfo = state.auth.userInfo;
        }
        
        if (!userInfo || !userInfo.unitId) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
            } catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
        if (userInfo && userInfo.unitId) {
          this.currentUnitId = userInfo.unitId;
} else {
          console.warn('未能获取当前用户单位ID');
          this.currentUnitId = null;
        }
      } catch (err) {
        console.error('获取用户unitId出错:', err);
        this.currentUnitId = null;
      }
    },
    
    // 获取当前用户关联的项目ID列表
    getCurrentUserProjectIds() {
      try {
        const store = this.$store || {};
        const state = store.state || {};
        
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          userInfo = state.user.userInfo;
        } else if (state.userInfo) {
          userInfo = state.userInfo;
        } else if (state.auth && state.auth.userInfo) {
          userInfo = state.auth.userInfo;
        }
        
        if (!userInfo) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
            } catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
        // 获取用户关联的项目ID列表
        if (userInfo && userInfo.projectIds) {
          return Array.isArray(userInfo.projectIds) ? userInfo.projectIds : [];
        } else if (userInfo && userInfo.projectId) {
          return [userInfo.projectId];
        } else {
          console.warn('未能获取用户关联的项目ID');
          return [];
        }
      } catch (err) {
        console.error('获取用户项目ID出错:', err);
        return [];
      }
    },
    
    // 获取订单详情
    getOrderDetail() {
      this.loading = true;
      get_order_detail(this.orderId)
        .then(response => {
          if (response.data && response.data.success) {
            const orderData = response.data.data;
// 处理订单信息
            this.orderInfo = orderData;
            
            // 处理订单材料项
            let materialsList = [];
            
            if (orderData.orderItems && Array.isArray(orderData.orderItems) && orderData.orderItems.length > 0) {
              materialsList = orderData.orderItems;
            } else if (orderData.OrderItems && Array.isArray(orderData.OrderItems) && orderData.OrderItems.length > 0) {
              materialsList = orderData.OrderItems.map(item => ({
                id: item.id,
                productName: item.product_name,
                specification: '',
                unit: item.product_unit,
                quantity: item.quantity,
                shippedQuantity: 0,
                remainingQuantity: item.quantity,
                taxInclusiveUnitPrice: item.product_tax_price,
                remark: item.remark || ''
              }));
            } else if (orderData.CustomerContract && orderData.CustomerContract.contractItems) {
              materialsList = orderData.CustomerContract.contractItems;
            } else if (orderData.contractDetails && orderData.contractDetails.contractItems) {
              materialsList = orderData.contractDetails.contractItems;
            }
            
            // 处理材料项，确保所有必需字段都存在
            this.orderMaterials = materialsList.map((item, index) => ({
              ...item,
              sequenceNumber: item.sequenceNumber || (index + 1),
              productName: item.materialsName || item.productName || item.materials_name || item.name || '未命名材料',
              specification: item.materialsSpec || item.specification || item.materials_spec || item.spec || '',
              unit: item.unit || item.product_unit || '个',
              quantity: item.quantity || 1,
              shippedQuantity: item.shippedQuantity || 0,
              remainingQuantity: item.remainingQuantity || (item.quantity || 1) - (item.shippedQuantity || 0),
              taxInclusiveUnitPrice: item.price || item.taxInclusiveUnitPrice || item.product_tax_price || 0,
              remark: item.remark || '',
              shipQuantity: 0 // 本次发货数量
            }));
            
// 计算总数量和总金额
            this.calculateTotals();
            
            // 加载发货记录
            this.getShipmentList();
            
          } else {
            this.$message.error(response.data?.message || '获取订单详情失败');
            this.goBack();
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('获取订单详情失败:', error);
          this.$message.error('获取订单详情失败: ' + (error.message || '未知错误'));
          this.loading = false;
          this.goBack();
        });
    },
    
    // 获取发货记录列表（分页查询）
    getShipmentList() {
const params = {
        orderId: this.orderId,
        page: this.pagination.page - 1, // 后端页码从0开始
        size: this.pagination.pageSize
      };
      
      query_logistics(params)
        .then(response => {
if (response.data && response.data.success) {
            const data = response.data.data;
this.shipmentList = data.content || [];
            this.pagination.total = data.totalElements || 0;
            
// 强制触发Vue响应式更新
            this.$forceUpdate();
            
            this.revertOrderStatusIfNoShipments();
          } else {
            console.warn('获取发货记录失败:', response.data?.message);
            this.shipmentList = [];
            this.pagination.total = 0;
            this.revertOrderStatusIfNoShipments();
          }
        })
        .catch(error => {
          console.error('获取发货记录失败:', error);
          this.shipmentList = [];
          this.pagination.total = 0;
        });
    },
    
    // 页码改变
    handleCurrentChange(currentPage) {
      this.pagination.page = currentPage;
      this.getShipmentList();
    },
    
    // 每页条数改变
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.page = 1;
      this.getShipmentList();
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return date;
        
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('日期格式化错误:', error);
        return date;
      }
    },
    
    // 获取需求单位名称
    getDemandUnits() {
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        const allUnits = [];
        this.orderInfo.contractDetails.forEach(contract => {
          if (contract.demandUnits && contract.demandUnits.length > 0) {
            allUnits.push(...contract.demandUnits.map(unit => unit.unitName));
          }
        });
        const uniqueUnits = [...new Set(allUnits)];
        return uniqueUnits.length > 0 ? uniqueUnits.join('，') : '未知单位';
      }
      return this.orderInfo.customerName || this.orderInfo.creatorName || '未知单位';
    },
    
    // 获取合同名称
    getContractNames() {
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        return this.orderInfo.contractDetails.map(contract => contract.contractName).join('，');
      }
      return this.orderInfo.contractName || '未知合同';
    },
    
    // 获取合同编号
    getContractNumbers() {
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        return this.orderInfo.contractDetails.map(contract => contract.contractNumber || contract.id).join('，');
      }
      return this.orderInfo.contractId || '未知编号';
    },
    
    // 获取项目名称（二级机构）
    getProjectNames() {
      // 优先从订单对象的secondaryOrgNames获取
      if (this.orderInfo.secondaryOrgNames && this.orderInfo.secondaryOrgNames.length > 0) {
        return this.orderInfo.secondaryOrgNames.join('，');
      }
      
      // 其次从secondaryOrgDetails获取
      if (this.orderInfo.secondaryOrgDetails && this.orderInfo.secondaryOrgDetails.length > 0) {
        const projectNames = this.orderInfo.secondaryOrgDetails
          .map(org => org.projectName)
          .filter(name => name);
        return projectNames.length > 0 ? projectNames.join('，') : '未关联项目';
      }
      
      // 最后从contractDetails中获取项目信息（作为备选）
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        const allProjects = [];
        this.orderInfo.contractDetails.forEach(contract => {
          if (contract.projects && contract.projects.length > 0) {
            allProjects.push(...contract.projects.map(project => project.projectName));
          }
        });
        const uniqueProjects = [...new Set(allProjects)];
        return uniqueProjects.length > 0 ? uniqueProjects.join('，') : '未关联项目';
      }
      
      return '未关联项目';
    },
    
    // 获取二级机构名称
    getSecondaryOrgNames() {
      // 优先从订单对象的secondaryOrgNames获取
      if (this.orderInfo.secondaryOrgNames && this.orderInfo.secondaryOrgNames.length > 0) {
        return this.orderInfo.secondaryOrgNames.join('，');
      }
      
      // 其次从secondaryOrgDetails获取
      if (this.orderInfo.secondaryOrgDetails && this.orderInfo.secondaryOrgDetails.length > 0) {
        const projectNames = this.orderInfo.secondaryOrgDetails
          .map(org => org.projectName)
          .filter(name => name);
        return projectNames.length > 0 ? projectNames.join('，') : '--';
      }
      
      // 最后从contractDetails中获取项目信息（作为备选）
      if (this.orderInfo.contractDetails && this.orderInfo.contractDetails.length > 0) {
        const allProjects = [];
        this.orderInfo.contractDetails.forEach(contract => {
          if (contract.projects && contract.projects.length > 0) {
            allProjects.push(...contract.projects.map(project => project.projectName));
          }
        });
        const uniqueProjects = [...new Set(allProjects)];
        return uniqueProjects.length > 0 ? uniqueProjects.join('，') : '--';
      }
      
      return '--';
    },
    
    // 获取收货地址
    getDeliveryAddress() {
      const savedAddress = this.orderInfo.deliveryAddress || this.orderInfo.contactAddress;
      if (savedAddress) {
        return savedAddress;
      }

      if (this.orderInfo.secondaryOrgDetails && this.orderInfo.secondaryOrgDetails.length > 0) {
        const addresses = this.orderInfo.secondaryOrgDetails
          .map(org => org.detailAddress)
          .filter(addr => addr)
          .join('，');
        if (addresses) {
          return addresses;
        }
      }

      return '--';
    },
    
    // 计算总数量和总价
    calculateTotals() {
      if (!this.orderMaterials || this.orderMaterials.length === 0) {
        this.totalQuantity = 0;
        this.totalPrice = 0;
        return;
      }
      
      // 计算总数量（支持4位小数）
      this.totalQuantity = this.orderMaterials.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity) || 0);
      }, 0);
      
      // 计算总价
      this.totalPrice = this.orderMaterials.reduce((sum, item) => {
        // 优先使用含税合价，如果没有则计算：含税单价 * 数量
        if (item.taxInclusiveTotalAmount) {
          return sum + parseFloat(item.taxInclusiveTotalAmount);
        }
        const price = parseFloat(item.taxInclusiveUnitPrice || item.price || 0);
        const quantity = parseFloat(item.quantity || 1);
        return sum + (price * quantity);
      }, 0).toFixed(2);
      
      // 如果订单信息中有总价，直接使用
      if (this.orderInfo && this.orderInfo.priceTotal) {
        this.totalPrice = parseFloat(this.orderInfo.priceTotal).toFixed(2);
      }
    },
    
    // 检查产品是否可选择
    checkSelectable(row) {
      if (this.isEditMode && this.currentEditProducts && this.currentEditProducts.length > 0) {
        // 编辑模式：原发货记录中的产品都可以选择
        return this.currentEditProducts.some(editProduct => editProduct.orderListId === row.id);
      } else {
        // 新增模式：只有剩余数量大于0的产品可以选择
        return row.remainingQuantity > 0;
      }
    },
    
    // 检查产品是否被选中
    isProductSelected(row) {
      return this.selectedProducts.some(item => item.id === row.id);
    },

    // 获取最大发货数量
    getMaxShipQuantity(row) {
      if (this.isEditMode && this.currentEditProducts && this.currentEditProducts.length > 0) {
        // 编辑模式：允许输入原发货数量 + 剩余数量
        const editProduct = this.currentEditProducts.find(editProduct => editProduct.orderListId === row.id);
        if (editProduct) {
          return (editProduct.shippedQuantity || 0) + (row.remainingQuantity || 0);
        }
        return row.remainingQuantity || 0;
      } else {
        // 新增模式：只能输入剩余数量
        return row.remainingQuantity || 0;
      }
    },
    
    
    // 选择变化
    handleSelectionChange(selection) {
      this.selectedProducts = selection;
    },
    
    // 数量变化
    handleQuantityChange(row) {
      const maxQuantity = this.getMaxShipQuantity(row);
      if (row.shipQuantity > maxQuantity) {
        if (this.isEditMode && this.currentEditProducts && this.currentEditProducts.length > 0) {
          const editProduct = this.currentEditProducts.find(editProduct => editProduct.orderListId === row.id);
          const originalQuantity = editProduct ? editProduct.shippedQuantity : 0;
          this.$message.warning(`发货数量不能超过${maxQuantity}（原发货数量${originalQuantity} + 剩余数量${row.remainingQuantity}）`);
        } else {
          this.$message.warning(`发货数量不能超过剩余数量${row.remainingQuantity}`);
        }
        row.shipQuantity = maxQuantity;
      }
      if (row.shipQuantity < 0) {
        row.shipQuantity = 0;
      }
    },
    
    // 获取状态标签类型
    getStatusType(status) {
      switch (status) {
        case 'ACTIVE':
          return 'warning';
        case 'EXECUTING':
          return 'primary';
        case 'SENDING':
          return 'success';
        case 'RECEIVING':
          return 'info';
        case 'END':
          return 'success';
        default:
          return 'info';
      }
    },
    
    // 获取订单类型标签类型
    getOrderTypeTagType(orderType) {
      switch (orderType) {
        case '02': return 'danger';
        case '01': default: return 'success';
      }
    },
    
    // 获取订单类型文本
    getOrderTypeText(orderType) {
      switch (orderType) {
        case '02': return '退货';
        case '01': default: return '正常';
      }
    },
    
    // 获取物流状态标签类型
    getLogisticsStatusType(status) {
      switch (status) {
        case '1':
          return 'success';
        case '2':
          return 'primary';
        case '3':
          return 'warning';
        case '4':
          return 'info';
        case '5':
          return 'danger';
        default:
          return 'info';
      }
    },
    
    // 提交发货
    handleSubmit() {
      // 如果是有物流信息模式，验证物流信息表单
      if (this.shipmentMode === 'withLogistics') {
        this.$refs.logisticsForm.validate((valid) => {
          if (!valid) {
            this.$message.error('请检查物流信息');
            return;
          }
          this.validateAndSubmit();
        });
      } else {
        // 无物流信息模式，直接验证产品选择
        this.validateAndSubmit();
      }
    },
    
    // 验证并提交
    validateAndSubmit() {
      // 补录模式下跳过产品选择验证
      if (!this.isSupplementMode) {
        // 验证是否选择了产品
        if (this.selectedProducts.length === 0) {
          this.$message.error('请至少选择一个产品进行发货');
          return;
        }
        
        // 验证发货数量
        const invalidProducts = this.selectedProducts.filter(item => {
          const maxQuantity = this.getMaxShipQuantity(item);
          return !item.shipQuantity || item.shipQuantity <= 0 || item.shipQuantity > maxQuantity;
        });
        
        if (invalidProducts.length > 0) {
          const errorMessage = this.isEditMode ? 
            '请检查发货数量，不能为0或超过允许的最大数量' : 
            '请检查发货数量，不能为0或超过剩余数量';
          this.$message.error(errorMessage);
          return;
        }
      }
      
      const confirmMessage = this.isSupplementMode ? '确认保存物流信息？' : '确认提交发货信息？';
      const confirmButtonText = this.isSupplementMode ? '确认保存' : '确认发货';
      const cancelMessage = this.isSupplementMode ? '已取消保存' : '已取消发货';
      
      this.$confirm(confirmMessage, '确认操作', {
        confirmButtonText: confirmButtonText,
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.submitShipment();
      }).catch(() => {
        this.$message.info(cancelMessage);
      });
    },
    
    // 显示新增发货对话框
    showCreateDialog() {
      this.isEditMode = false;
      this.isSupplementMode = false;
      this.currentEditLogistics = null;
      this.currentEditProducts = []; // 清空编辑模式的产品列表
      this.shipmentMode = 'withLogistics'; // 重置为有物流信息模式
      this.resetLogisticsForm();
      this.resetProductSelection();
      this.shipmentDialogVisible = true;
    },
    
    // 补录物流信息
    async supplementLogistics(logisticsInfo) {
      this.isEditMode = false;
      this.isSupplementMode = true;
      this.currentEditLogistics = logisticsInfo;
      this.currentEditProducts = [];
      
      // 根据现有物流信息判断发货方式
      if (logisticsInfo.trackingNumber === '00000000' && logisticsInfo.logisticsCompany === '无') {
        this.shipmentMode = 'withoutLogistics';
      } else {
        this.shipmentMode = 'withLogistics';
      }
      
      // 填充物流信息
      this.logisticsForm = {
        trackingNumber: logisticsInfo.trackingNumber,
        logisticsCompany: logisticsInfo.logisticsCompany,
        shipTime: logisticsInfo.shipTime,
        estimatedArrivalTime: logisticsInfo.estimatedArrivalTime,
        remark: logisticsInfo.remark
      };
      
      // 获取该物流记录的发货清单（补录时需要传回后台）
      try {
        const response = await get_shipment_list_by_logistics_id(logisticsInfo.id);
        if (response.data && response.data.success) {
          this.currentEditProducts = response.data.data || [];
} else {
          console.warn('获取发货清单失败:', response.data?.message);
          this.currentEditProducts = [];
        }
      } catch (error) {
        console.error('获取发货清单失败:', error);
        this.currentEditProducts = [];
      }
      
      // 清空产品选择
      this.resetProductSelection();
      
      this.shipmentDialogVisible = true;
    },
    
    // 编辑发货
    async editShipment(logisticsInfo) {
      this.isEditMode = true;
      this.isSupplementMode = false;
      this.currentEditLogistics = logisticsInfo;
      
      // 根据现有物流信息判断发货方式
      if (logisticsInfo.trackingNumber === '00000000' && logisticsInfo.logisticsCompany === '无') {
        this.shipmentMode = 'withoutLogistics';
      } else {
        this.shipmentMode = 'withLogistics';
      }
      
      // 填充物流信息
      this.logisticsForm = {
        trackingNumber: logisticsInfo.trackingNumber,
        logisticsCompany: logisticsInfo.logisticsCompany,
        shipTime: logisticsInfo.shipTime,
        estimatedArrivalTime: logisticsInfo.estimatedArrivalTime,
        remark: logisticsInfo.remark
      };
      
      // 获取该物流记录的发货清单
      try {
        const response = await get_shipment_list_by_logistics_id(logisticsInfo.id);
        if (response.data && response.data.success) {
          const shipmentItems = response.data.data || [];
// 保存当前编辑的发货产品列表
          this.currentEditProducts = shipmentItems;
          
          // 先重置产品选择
          this.resetProductSelection();
          
          // 回显选中的产品和数量
          shipmentItems.forEach(item => {
            // 根据 orderListId 找到对应的产品
            const product = this.orderMaterials.find(p => p.id === item.orderListId);
            if (product) {
              // 设置发货数量
              product.shipQuantity = item.shippedQuantity;
              // 添加到选中列表
              this.selectedProducts.push(product);
            }
          });
          
// 使用 nextTick 确保 DOM 更新后再设置表格选择状态
          this.$nextTick(() => {
            if (this.$refs.productTable) {
              // 先清除所有选择
              this.$refs.productTable.clearSelection();
              
              // 等待一小段时间确保清除完成，然后设置选中状态
              setTimeout(() => {
                this.selectedProducts.forEach(product => {
                  this.$refs.productTable.toggleRowSelection(product, true);
                });
}, 50);
            }
          });
        } else {
          console.warn('获取发货清单失败:', response.data?.message);
          this.resetProductSelection();
        }
      } catch (error) {
        console.error('获取发货清单失败:', error);
        this.$message.error('获取发货清单失败: ' + (error.message || '未知错误'));
        this.resetProductSelection();
      }
      
      this.shipmentDialogVisible = true;
    },
    
    // 删除发货
    deleteShipment(logisticsInfo) {
      this.$confirm('确认删除该发货记录？此操作不可恢复！', '删除确认', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.doDeleteShipment(logisticsInfo);
      }).catch(() => {
        this.$message.info('已取消删除');
      });
    },
    
    // 执行删除发货
    async doDeleteShipment(logisticsInfo) {
      try {
        await delete_logistics(logisticsInfo.id);
        this.$message.success('删除成功');
        
        // 重新查询订单信息，确保已发数量得到正确更新
        await this.getOrderDetail();
        
        // 刷新发货记录列表
        this.getShipmentList();
      } catch (error) {
        console.error('删除发货记录失败:', error);
        this.$message.error('删除发货记录失败: ' + (error.message || '未知错误'));
      }
    },
    
    // 查看发货详情
    async viewShipmentDetails(logisticsInfo) {
      this.currentShipmentDetail = logisticsInfo;
      
      try {
        const response = await get_shipment_list_by_logistics_id(logisticsInfo.id);
        if (response.data && response.data.success) {
          this.currentShipmentDetail.shipmentItems = response.data.data || [];
        } else {
          console.warn('获取发货清单失败:', response.data?.message);
          this.currentShipmentDetail.shipmentItems = [];
        }
      } catch (error) {
        console.error('获取发货清单详情失败:', error);
        this.currentShipmentDetail.shipmentItems = [];
      }
      
      this.detailDialogVisible = true;
    },

    openAttachmentDialog(logisticsInfo) {
      this.currentAttachmentLogistics = logisticsInfo;
      this.activeAttachmentTab = 'LOGISTICS';
      this.attachmentDialogVisible = true;
    },
    
    // 更新物流状态
    updateLogisticsStatus(logisticsInfo, newStatus) {
      const statusMap = {
        '2': '运输中',
        '3': '已到达',
        '4': '已签收'
      };
      
      this.$confirm(`确认将物流状态更新为"${statusMap[newStatus]}"？`, '状态更新确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.doUpdateLogisticsStatus(logisticsInfo, newStatus);
      }).catch(() => {
        this.$message.info('已取消操作');
      });
    },
    
    // 执行更新物流状态
    async doUpdateLogisticsStatus(logisticsInfo, newStatus) {
      try {
        const statusMap = {
          '2': '运输中',
          '3': '已到达',
          '4': '已签收'
        };
        
        await update_logistics_status(logisticsInfo.id, newStatus, statusMap[newStatus]);
        this.$message.success('状态更新成功');
        this.getShipmentList();
      } catch (error) {
        console.error('更新物流状态失败:', error);
        this.$message.error('更新物流状态失败: ' + (error.message || '未知错误'));
      }
    },
    
    // 判断是否可以编辑发货
    canEditShipment(logisticsInfo) {
      // 只有未签收的发货记录可以编辑
      return !logisticsInfo.isReceived && logisticsInfo.logisticsStatus !== '4';
    },
    
    // 判断是否可以删除发货
    canDeleteShipment(logisticsInfo) {
      // 只有未签收的发货记录可以删除
      return !logisticsInfo.isReceived && logisticsInfo.logisticsStatus !== '4';
    },
    
    // 重置物流表单
    resetLogisticsForm() {
      this.logisticsForm = {
        trackingNumber: '',
        logisticsCompany: '',
        shipTime: this.getCurrentDateTimeValue(),
        estimatedArrivalTime: '',
        remark: ''
      };
      this.$nextTick(() => {
        if (this.$refs.logisticsForm) {
          this.$refs.logisticsForm.clearValidate();
        }
      });
    },

    getCurrentDateTimeValue() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    },
    
    // 重置产品选择
    resetProductSelection() {
      this.selectedProducts = [];
      this.orderMaterials.forEach(item => {
        item.shipQuantity = 0;
      });
      
      // 清理表格的选中状态
      this.$nextTick(() => {
        if (this.$refs.productTable) {
          this.$refs.productTable.clearSelection();
        }
      });
    },
    
    // 发货方式变化处理
    handleShipmentModeChange(mode) {
      if (mode === 'withoutLogistics') {
        // 无物流信息模式：设置默认值
        const currentTime = new Date();
        const year = currentTime.getFullYear();
        const month = String(currentTime.getMonth() + 1).padStart(2, '0');
        const day = String(currentTime.getDate()).padStart(2, '0');
        const hours = String(currentTime.getHours()).padStart(2, '0');
        const minutes = String(currentTime.getMinutes()).padStart(2, '0');
        const seconds = String(currentTime.getSeconds()).padStart(2, '0');
        const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        
        this.logisticsForm = {
          trackingNumber: '00000000', // 8个0
          logisticsCompany: '无', // 物流公司为无
          shipTime: formattedDateTime, // 当前时间
          estimatedArrivalTime: '',
          remark: ''
        };
      } else {
        // 有物流信息模式：重置表单
        this.resetLogisticsForm();
      }
    },
    
    // 关闭对话框
    handleDialogClose() {
      this.shipmentDialogVisible = false;
      this.shipmentMode = 'withLogistics'; // 重置发货方式
      this.isSupplementMode = false; // 重置补录模式
      this.resetLogisticsForm();
      this.resetProductSelection();
    },
    
    // 执行发货提交
    async submitShipment() {
      this.submitting = true;
      
      try {
        if (this.isSupplementMode) {
          // 补录模式 - 只更新物流信息
          await this.updateLogisticsInfo();
        } else if (this.isEditMode) {
          // 编辑模式 - 更新现有发货记录
          await this.updateShipment();
        } else {
          // 新增模式 - 创建新发货记录
          await this.createShipment();
        }
        
        this.shipmentDialogVisible = false;
        
        // 重新查询订单信息，确保已发数量得到正确更新
        await this.getOrderDetail();
        
        // 刷新发货记录列表
        this.getShipmentList();
        
      } catch (error) {
        console.error('发货提交失败:', error);
        this.$message.error('发货提交失败: ' + (error.message || '未知错误'));
      } finally {
        this.submitting = false;
      }
    },
    
    // 创建发货记录
    async createShipment() {
      // 准备物流信息数据
      const logisticsInfo = {
        orderId: this.orderId,
        trackingNumber: this.logisticsForm.trackingNumber,
        logisticsCompany: this.logisticsForm.logisticsCompany,
        shipTime: this.logisticsForm.shipTime,
        estimatedArrivalTime: this.logisticsForm.estimatedArrivalTime,
        logisticsStatus: '1', // 已发货
        logisticsStatusDesc: '已发货',
        remark: this.logisticsForm.remark
      };
      
      // 准备发货清单数据
      const shipmentItems = this.selectedProducts.map((item, index) => ({
        sequenceNumber: index + 1,
        orderListId: item.id,
        productName: item.productName,
        specification: item.specification,
        materialRequirement: item.materialRequirement || '',
        unit: item.unit,
        shippedQuantity: item.shipQuantity,
        pricingMethod: item.pricingMethod || 'FIXED',
        taxExclusiveUnitPrice: item.taxExclusiveUnitPrice || 0,
        taxInclusiveUnitPrice: item.taxInclusiveUnitPrice || 0,
        taxInclusiveBasePrice: item.taxInclusiveBasePrice || null,
        taxInclusiveFloatingPrice: item.taxInclusiveFloatingPrice || null,
        taxInclusiveTotalAmount: this.preciseMultiply(item.taxInclusiveUnitPrice, item.shipQuantity),
        taxRate: item.taxRate || 0,
        remark: item.remark,
        shipmentDate: this.logisticsForm.shipTime ? this.logisticsForm.shipTime.split('T')[0] : null,
        productSupplierId: item.productSupplierId
      }));
      
      // 使用批量保存接口
      const batchData = {
        logisticsInfo,
        shipmentItems
      };
      
const response = await create_logistics_with_shipment(batchData);
      
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || '发货登记失败');
      }
      
// 检查并更新订单状态
      await this.updateOrderStatusIfNeeded();
      
      this.$message.success('发货登记成功');
    },
    
    // 无发货记录时将订单从已发货恢复为执行中
    async revertOrderStatusIfNoShipments() {
      if (!this.orderInfo || !this.orderId) {
        return;
      }
      if (this.orderInfo.status !== 'SENDING') {
        return;
      }
      const noShipments = this.pagination.total === 0 &&
        (!this.shipmentList || this.shipmentList.length === 0);
      if (!noShipments) {
        return;
      }
      try {
        const response = await update_order_status(this.orderId, 'EXECUTING');
        if (response.data && response.data.success) {
          this.orderInfo.status = 'EXECUTING';
}
      } catch (error) {
        console.error('恢复订单状态为执行中时出错:', error);
      }
    },
    
    // 检查并更新订单状态
    async updateOrderStatusIfNeeded() {
      try {
        // 检查当前订单状态
        const currentStatus = this.orderInfo.status;
// 如果订单状态是"执行中"，则更新为"已发货"
        if (currentStatus === 'EXECUTING') {
const response = await update_order_status(this.orderId, 'SENDING');
          
          if (response.data && response.data.success) {
// 更新本地订单信息
            this.orderInfo.status = 'SENDING';
            this.$message.success('订单状态已更新为已发货');
          } else {
            console.warn('订单状态更新失败:', response.data?.message);
            // 不显示错误消息，因为发货已经成功，状态更新失败不影响主要功能
          }
        }
      } catch (error) {
        console.error('更新订单状态时出错:', error);
        // 不显示错误消息，因为发货已经成功，状态更新失败不影响主要功能
      }
    },
    
    // 更新发货记录
    async updateShipment() {
      // 准备物流信息数据（包含ID用于更新）
      const logisticsInfo = {
        id: this.currentEditLogistics.id, // 包含ID表示更新操作
        orderId: this.orderId,
        trackingNumber: this.logisticsForm.trackingNumber,
        logisticsCompany: this.logisticsForm.logisticsCompany,
        shipTime: this.logisticsForm.shipTime,
        estimatedArrivalTime: this.logisticsForm.estimatedArrivalTime,
        logisticsStatus: this.currentEditLogistics.logisticsStatus, // 保持原状态
        logisticsStatusDesc: this.currentEditLogistics.logisticsStatusText, // 保持原状态描述
        remark: this.logisticsForm.remark
      };
      
      // 准备发货清单数据
      const shipmentItems = this.selectedProducts.map((product, index) => ({
        sequenceNumber: index + 1,
        orderListId: product.id,
        productName: product.productName,
        specification: product.specification,
        materialRequirement: product.materialRequirement || '',
        unit: product.unit,
        shippedQuantity: product.shipQuantity,
        pricingMethod: product.pricingMethod || 'FIXED',
        taxExclusiveUnitPrice: product.taxExclusiveUnitPrice || 0,
        taxInclusiveUnitPrice: product.taxInclusiveUnitPrice || 0,
        taxInclusiveBasePrice: product.taxInclusiveBasePrice || null,
        taxInclusiveFloatingPrice: product.taxInclusiveFloatingPrice || null,
        taxInclusiveTotalAmount: this.preciseMultiply(product.taxInclusiveUnitPrice, product.shipQuantity),
        taxRate: product.taxRate || 0,
        remark: product.remark,
        shipmentDate: this.logisticsForm.shipTime ? this.logisticsForm.shipTime.split('T')[0] : null,
        productSupplierId: product.productSupplierId
      }));
      
      // 使用批量保存接口（更新模式）
      const batchData = {
        logisticsInfo,
        shipmentItems
      };
      
const response = await create_logistics_with_shipment(batchData);
      
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || '更新发货记录失败');
      }
      
this.$message.success('更新发货记录成功');
    },
    
    // 更新物流信息（补录模式）
    async updateLogisticsInfo() {
      // 打印当前物流信息，用于调试签收字段
// 准备物流信息数据，保留原有的签收信息
      const logisticsInfo = {
        id: this.currentEditLogistics.id,
        orderId: this.orderId,
        trackingNumber: this.logisticsForm.trackingNumber,
        logisticsCompany: this.logisticsForm.logisticsCompany,
        shipTime: this.logisticsForm.shipTime,
        estimatedArrivalTime: this.logisticsForm.estimatedArrivalTime,
        logisticsStatus: this.currentEditLogistics.logisticsStatus,
        logisticsStatusDesc: this.currentEditLogistics.logisticsStatusText,
        remark: this.logisticsForm.remark,
        // 保留原有的签收信息
        isReceived: this.currentEditLogistics.isReceived,
        receivedBy: this.currentEditLogistics.receivedBy,
        receivedPhone: this.currentEditLogistics.receivedPhone,
        receivedTime: this.currentEditLogistics.receivedTime,
        receivedCertificate: this.currentEditLogistics.receivedCertificate,
        // 可能的其他签收相关字段
        actualArrivalTime: this.currentEditLogistics.actualArrivalTime,
        receiverName: this.currentEditLogistics.receiverName,
        receiverPhone: this.currentEditLogistics.receiverPhone
      };
      
      // 准备发货清单数据（使用原有数据，不允许修改）
      const shipmentItems = this.currentEditProducts.map((item, index) => ({
        sequenceNumber: index + 1,
        orderListId: item.orderListId,
        productName: item.productName,
        specification: item.specification,
        materialRequirement: item.materialRequirement || '',
        unit: item.unit,
        shippedQuantity: item.shippedQuantity,
        pricingMethod: 'FIXED',
        taxExclusiveUnitPrice: item.taxExclusiveUnitPrice || 0,
        taxInclusiveUnitPrice: item.taxInclusiveUnitPrice,
        taxInclusiveTotalAmount: item.taxInclusiveTotalAmount,
        taxRate: item.taxRate || 0,
        remark: item.remark,
        shipmentDate: item.shipmentDate,
        productSupplierId: item.productSupplierId
      }));
      
      // 使用批量保存接口进行补录
      const batchData = {
        logisticsInfo,
        shipmentItems
      };
      
const response = await create_logistics_with_shipment(batchData);
      
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || '补录物流信息失败');
      }
      
this.$message.success('物流信息补录成功');
    },
    
    // 获取订单联系人信息
    getOrderContactInfo() {
      const contactPerson = this.orderInfo.demandContactPerson || this.orderInfo.contactPerson || '';
      const contactPhone = this.orderInfo.demandContactPhone || this.orderInfo.contactPhone || '';
      return { contactPerson, contactPhone };
    },
    
    // 验证签收人姓名
    validateReceivedBy(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      
      const { contactPerson } = this.getOrderContactInfo();
      if (!contactPerson) {
        // 如果订单中没有联系人信息，则跳过验证
        callback();
        return;
      }
      
      if (value !== contactPerson) {
        callback(new Error('签收人姓名与订单联系人不符'));
      } else {
        callback();
      }
    },
    
    // 验证签收人电话
    validateReceivedPhone(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }
      
      const { contactPhone } = this.getOrderContactInfo();
      if (!contactPhone) {
        // 如果订单中没有联系电话信息，则跳过验证
        callback();
        return;
      }
      
      if (value !== contactPhone) {
        callback(new Error('签收人电话与订单联系电话不符'));
      } else {
        callback();
      }
    },
    
    // 获取当前登录用户的显示名称（用于判断是否为签收人）
    getCurrentUserDisplayName() {
      try {
        const store = this.$store || {};
        const state = store.state || {};
        let userInfo = state.user?.userInfo || state.userInfo || state.auth?.userInfo;
        if (!userInfo) {
          const local = localStorage.getItem('userInfo');
          if (local) {
            try {
              userInfo = JSON.parse(local);
            } catch (e) {
              return '';
            }
          }
        }
        const name = (userInfo?.realName || userInfo?.real_name || userInfo?.username || '').trim();
        return name;
      } catch (err) {
        return '';
      }
    },
    
    // 判断当前用户是否为该发货记录的签收人
    isReceiver(shipment) {
      if (!shipment || !shipment.isReceived || !shipment.receivedBy) return false;
      const currentName = this.getCurrentUserDisplayName();
      if (!currentName) return false;
      const receivedBy = (shipment.receivedBy || '').trim();
      return receivedBy === currentName;
    },
    
    // 打开修改收货时间对话框
    handleEditReceivedTime(row) {
      this.currentEditReceivedLogistics = row;
      const receivedTime = row.receivedTime || '';
      // 将 ISO 格式转为 yyyy-MM-dd HH:mm:ss
      let displayTime = receivedTime;
      if (receivedTime && receivedTime.includes('T')) {
        displayTime = receivedTime.replace('T', ' ');
      }
      this.editReceivedTimeForm = { receivedTime: displayTime || '' };
      this.editReceivedTimeDialogVisible = true;
    },
    
    // 确认修改收货时间
    async confirmEditReceivedTime() {
      this.$refs.editReceivedTimeForm.validate((valid) => {
        if (valid) {
          this.doEditReceivedTime();
        }
      });
    },
    
    // 执行修改收货时间
    async doEditReceivedTime() {
      if (!this.currentEditReceivedLogistics) return;
      this.editingReceivedTime = true;
      try {
        const receivedTime = this.editReceivedTimeForm.receivedTime;
        const isoTime = receivedTime ? receivedTime.replace(' ', 'T') : null;
        const { update_logistics_received_time } = await import('@/api/modules/logistics.js');
        await update_logistics_received_time(this.currentEditReceivedLogistics.id, isoTime);
        this.$message.success('收货时间修改成功');
        this.editReceivedTimeDialogVisible = false;
        this.getShipmentList();
      } catch (error) {
        console.error('修改收货时间失败:', error);
        this.$message.error('修改收货时间失败: ' + (error.message || '未知错误'));
      } finally {
        this.editingReceivedTime = false;
      }
    },
    
    // 处理收货
    handleReceive(logisticsInfo) {
      this.currentReceiveLogistics = logisticsInfo;
      // 默认收货日期为当前时间
      const now = new Date();
      const defaultDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      this.receiveForm = {
        receivedBy: '',
        receivedPhone: '',
        receivedTime: defaultDate
      };
      this.receiveDialogVisible = true;
    },
    
    // 确认收货
    confirmReceive() {
      this.$refs.receiveForm.validate((valid) => {
        if (valid) {
          this.doReceive();
        }
      });
    },
    
    // 执行收货
    async doReceive() {
      this.receiving = true;
      
      try {
        const response = await receive_logistics(
          this.currentReceiveLogistics.id,
          this.receiveForm.receivedBy,
          this.receiveForm.receivedPhone,
          null, // 签收凭证，当前不需要
          this.receiveForm.receivedTime || null // 用户选择的收货日期
        );
        
        if (response.data && response.data.success) {
          this.$message.success('收货确认成功');
          this.receiveDialogVisible = false;
          
          // 刷新发货记录列表
          this.getShipmentList();
        } else {
          this.$message.error(response.data?.message || '收货确认失败');
        }
      } catch (error) {
        console.error('收货确认失败:', error);
        this.$message.error('收货确认失败: ' + (error.message || '未知错误'));
      } finally {
        this.receiving = false;
      }
    },
    
    // 确认全部已收货
    confirmAllReceived() {
      // 检查是否还有未签收的发货记录
      const unReceivedShipments = this.shipmentList.filter(shipment => !shipment.isReceived);
      
      if (unReceivedShipments.length > 0) {
        // 构建未签收记录的详细信息
        const unReceivedDetails = unReceivedShipments.map(shipment => 
          `• 物流单号：${shipment.trackingNumber}（${shipment.logisticsCompany}）`
        ).join('\n');
        
        // 直接提示用户，不允许继续操作
        this.$alert(
          `无法确认全部已收货，还有 ${unReceivedShipments.length} 条发货记录未签收：\n\n${unReceivedDetails}\n\n请确保所有发货记录都已签收后再进行操作。`, 
          '提示', 
          {
            confirmButtonText: '确定',
            type: 'warning'
          }
        );
        return; // 直接返回，不允许继续操作
      }
      
      // 所有发货记录都已签收，显示确认对话框
      this.$confirm('所有发货记录均已签收，确认将订单状态更改为"已收货"？此操作不可撤销。', '确认提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.doConfirmAllReceived();
      }).catch(() => {
        this.$message.info('已取消操作');
      });
    },
    
    // 执行确认全部已收货
    async doConfirmAllReceived() {
      this.confirmingAll = true;
      
      try {
        // 调用更新订单状态API
        const { update_order_status } = await import('@/api/modules/orders.js');
        const response = await update_order_status(this.orderId, 'RECEIVING');
        
        if (response.data && response.data.success) {
          this.$message.success('订单状态已更新为已收货');
          
          // 更新本地订单状态
          this.orderInfo.status = 'RECEIVING';
          
          // 刷新发货记录列表
          this.getShipmentList();
        } else {
          this.$message.error(response.data?.message || '订单状态更新失败');
        }
      } catch (error) {
        console.error('订单状态更新失败:', error);
        this.$message.error('订单状态更新失败: ' + (error.message || '未知错误'));
      } finally {
        this.confirmingAll = false;
      }
    },
    
    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0.00';
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 格式化单价（保留8位小数，去除末尾无意义的0）
    formatUnitPrice(amount) {
      if (!amount && amount !== 0) return '0';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0';
      // 先格式化为8位小数，然后去除末尾的0
      const formatted = num.toFixed(8);
      // 去除末尾的0和小数点（如果有）
      return formatted.replace(/\.?0+$/, '') || '0';
    },
    
    // 格式化数量（保留4位小数，去除末尾无意义的0）
    formatQuantity(quantity) {
      if (quantity === undefined || quantity === null) return '0';
      const num = parseFloat(quantity);
      if (isNaN(num)) return '0';
      // 先格式化为4位小数，然后去除末尾的0和小数点（如果有）
      const formatted = num.toFixed(4);
      return formatted.replace(/\.?0+$/, '') || '0';
    },
    
    // 精确计算乘法，避免浮点数精度问题
    preciseMultiply(a, b) {
      const aNum = parseFloat(a) || 0;
      const bNum = parseFloat(b) || 0;
      
      // 将数字转换为字符串，计算小数位数
      const aStr = aNum.toString();
      const bStr = bNum.toString();
      
      const aDecimalPlaces = aStr.includes('.') ? aStr.split('.')[1].length : 0;
      const bDecimalPlaces = bStr.includes('.') ? bStr.split('.')[1].length : 0;
      
      // 计算总的小数位数
      const totalDecimalPlaces = aDecimalPlaces + bDecimalPlaces;
      
      // 将数字转换为整数进行计算
      const aInt = parseInt(aStr.replace('.', ''));
      const bInt = parseInt(bStr.replace('.', ''));
      
      const result = aInt * bInt;
      
      // 将结果除以10的总小数位数次方
      const divisor = Math.pow(10, totalDecimalPlaces);
      const finalResult = result / divisor;
      
      // 保留2位小数
      return Math.round(finalResult * 100) / 100;
    },
    
    // 返回上一页
    goBack() {
      this.$router.go(-1);
    },
    
    // 获取需求单位的签名图片URL
    async getDemandUnitSignatureUrl() {
      try {
        // 获取需求单位的联系人信息
        const contactPerson = this.orderInfo.demandContactPerson || this.orderInfo.contactPerson;
        const contactPhone = this.orderInfo.demandContactPhone || this.orderInfo.contactPhone;
        
        if (!contactPerson && !contactPhone) {
          console.warn('未找到需求单位联系人信息');
          return null;
        }
        
        // 优先根据电话查询（更精确）
        if (contactPhone) {
          try {
            const response = await query_users_pagination({
              phone: contactPhone,
              page: 0,
              size: 10
            });
            
            if (response.data && response.data.success) {
              const users = response.data.data?.content || response.data.data || [];
              // 查找匹配的用户（电话精确匹配）
              const matchedUser = users.find(user => 
                user.phone === contactPhone && user.signatureImagePath
              );
              
              if (matchedUser && matchedUser.signatureImagePath) {
                return this.buildSignatureImageUrl(matchedUser.signatureImagePath);
              }
            }
          } catch (error) {
            console.warn('根据电话查询用户失败:', error);
          }
        }
        
        // 如果电话查询不到，根据联系人姓名查询
        if (contactPerson) {
          try {
            const response = await query_users_pagination({
              username: contactPerson,
              page: 0,
              size: 10
            });
            
            if (response.data && response.data.success) {
              const users = response.data.data?.content || response.data.data || [];
              // 查找匹配的用户（用户名精确匹配或包含）
              const matchedUser = users.find(user => 
                (user.username === contactPerson || user.username.includes(contactPerson)) && 
                user.signatureImagePath
              );
              
              if (matchedUser && matchedUser.signatureImagePath) {
                return this.buildSignatureImageUrl(matchedUser.signatureImagePath);
              }
            }
          } catch (error) {
            console.warn('根据姓名查询用户失败:', error);
          }
        }
        
        return null;
      } catch (error) {
        console.error('获取需求单位签名图片失败:', error);
        return null;
      }
    },
    
    // 获取供应商单位的签名图片URL
    async getSupplierSignatureUrl() {
      try {
        // 获取供应商单位的联系人信息
        // 注意：orderInfo.contactPerson 和 orderInfo.contactPhone 可能是供应商的联系人
        // 需要确认订单信息中供应商联系人的字段名
        const supplierContactPerson = this.orderInfo.supplierContactPerson || 
                                      (this.orderInfo.supplierDetails && this.orderInfo.supplierDetails.contactPerson) ||
                                      this.orderInfo.contactPerson;
        const supplierContactPhone = this.orderInfo.supplierContactPhone || 
                                     (this.orderInfo.supplierDetails && this.orderInfo.supplierDetails.phone) ||
                                     this.orderInfo.contactPhone;
        
        if (!supplierContactPerson && !supplierContactPhone) {
          console.warn('未找到供应商单位联系人信息');
          return null;
        }
        
        // 优先根据电话查询（更精确）
        if (supplierContactPhone) {
          try {
            const response = await query_users_pagination({
              phone: supplierContactPhone,
              page: 0,
              size: 10
            });
            
            if (response.data && response.data.success) {
              const users = response.data.data?.content || response.data.data || [];
              // 查找匹配的用户（电话精确匹配）
              const matchedUser = users.find(user => 
                user.phone === supplierContactPhone && user.signatureImagePath
              );
              
              if (matchedUser && matchedUser.signatureImagePath) {
                return this.buildSignatureImageUrl(matchedUser.signatureImagePath);
              }
            }
          } catch (error) {
            console.warn('根据电话查询供应商联系人失败:', error);
          }
        }
        
        // 如果电话查询不到，根据联系人姓名查询
        if (supplierContactPerson) {
          try {
            const response = await query_users_pagination({
              username: supplierContactPerson,
              page: 0,
              size: 10
            });
            
            if (response.data && response.data.success) {
              const users = response.data.data?.content || response.data.data || [];
              // 查找匹配的用户（用户名精确匹配或包含）
              const matchedUser = users.find(user => 
                (user.username === supplierContactPerson || user.username.includes(supplierContactPerson)) && 
                user.signatureImagePath
              );
              
              if (matchedUser && matchedUser.signatureImagePath) {
                return this.buildSignatureImageUrl(matchedUser.signatureImagePath);
              }
            }
          } catch (error) {
            console.warn('根据姓名查询供应商联系人失败:', error);
          }
        }
        
        return null;
      } catch (error) {
        console.error('获取供应商单位签名图片失败:', error);
        return null;
      }
    },
    
    // 构建签名图片URL
    buildSignatureImageUrl(filePath) {
      if (!filePath) {
        return null;
      }
      
      // 如果是完整的URL（http/https开头），直接使用
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
      }
      
      // 如果是data URL（base64），直接使用
      if (filePath.startsWith('data:')) {
        return filePath;
      }
      
      // 否则，使用附件下载接口
      const apiUrl = getApiUrl('/attachments/download');
      return `${apiUrl}?filePath=${encodeURIComponent(filePath)}`;
    },
    
    // 打印送货单
    async printDeliveryNote(logisticsInfo) {
      try {
        // 获取该物流记录的发货清单
        const response = await get_shipment_list_by_logistics_id(logisticsInfo.id);
        const shipmentItems = response.data && response.data.success ? response.data.data || [] : [];

        const deliveryNoteResponse = await get_or_create_delivery_note(logisticsInfo.id);
        if (!deliveryNoteResponse.data || !deliveryNoteResponse.data.success) {
          throw new Error(deliveryNoteResponse.data?.message || '获取送货单编号失败');
        }
        const deliveryNoteInfo = deliveryNoteResponse.data.data || {};
        
        // 获取需求单位的签名图片URL（仅在已签收时才获取）
        const signatureImageUrl = logisticsInfo.isReceived ? await this.getDemandUnitSignatureUrl() : null;
        
        // 获取供应商单位的签名图片URL
        const supplierSignatureImageUrl = await this.getSupplierSignatureUrl();
        
        // 准备送货单内容
        const supplierName = this.orderInfo.supplierDetails && this.orderInfo.supplierDetails.unitName || this.orderInfo.supplierName || '四川三华智建企业管理有限公司';
        const demandUnitName = this.getDemandUnits();
        const secondaryOrg = this.getSecondaryOrgNames();
        const demandContactPerson = this.maskName(this.orderInfo.demandContactPerson || this.orderInfo.contactPerson) || '--';
        const demandContactPhone = this.maskPhone(this.orderInfo.demandContactPhone || this.orderInfo.contactPhone) || '--';
        const deliveryAddress = this.getDeliveryAddress();
        const supplierContactPerson = this.maskName(
          this.orderInfo.supplierContactPerson ||
          (this.orderInfo.supplierDetails && this.orderInfo.supplierDetails.contactPerson) ||
          this.orderInfo.contactPerson
        ) || '--';
        const supplierContactPhone = this.maskPhone(
          this.orderInfo.supplierContactPhone ||
          (this.orderInfo.supplierDetails && this.orderInfo.supplierDetails.phone) ||
          this.orderInfo.contactPhone
        ) || '--';
        
        // 判断价格列标题和数据
        const priceColumnTitle = this.hasFloatingPriceContract ? '含税变动价(元)' : '合同价';
        const getPriceValue = (item) => {
          if (this.hasFloatingPriceContract) {
            return this.formatAmount(item.taxInclusiveFloatingPrice || 0);
          } else {
            return this.formatUnitPrice(item.taxInclusiveUnitPrice || 0);
          }
        };
        
        // 构建需求单位签名图片HTML
        const signatureHtml = signatureImageUrl 
          ? `<img src="${signatureImageUrl}" style="max-width: 150px; max-height: 80px; object-fit: contain; display: inline-block; vertical-align: middle; margin-left: 10px;" alt="签名" />`
          : '';
        
        // 构建供应商签名图片HTML
        const supplierSignatureHtml = supplierSignatureImageUrl 
          ? `<img src="${supplierSignatureImageUrl}" style="max-width: 150px; max-height: 80px; object-fit: contain; display: inline-block; vertical-align: middle; margin-left: 10px;" alt="签名" />`
          : '';
        
        const orderTitle = deliveryNoteInfo.orderTitle || this.orderInfo.orderTitle || this.orderInfo.order_title || '';
        const orderNumber = deliveryNoteInfo.orderNumber || this.orderInfo.orderId || this.orderInfo.order_id || '';
        const deliveryNoteNumber = deliveryNoteInfo.deliveryNoteNumber || logisticsInfo.deliveryNoteNumber || '--';
        const shipmentDate = deliveryNoteInfo.shipmentDate || logisticsInfo.shipTime?.split('T')[0] || '--';
        const trackingNumber = logisticsInfo.trackingNumber || '';
        const logisticsCompany = logisticsInfo.logisticsCompany || '';

        // 创建送货单HTML内容
        const deliveryNoteHTML = `
          <div class="delivery-note">
            <div class="doc-header">
              <h1 class="doc-company">${supplierName}</h1>
              <h2 class="doc-title">送货单</h2>
              <div class="doc-header-meta">
                <span></span>
                <span class="doc-ship-date">发货日期：${shipmentDate}</span>
                <span class="doc-number">送货单编号：${deliveryNoteNumber}</span>
              </div>
            </div>

            <table class="print-table meta-table">
              <tr>
                <td class="cell-label">订单名称</td>
                <td class="cell-value">${orderTitle}</td>
                <td class="cell-label">关联订单号</td>
                <td class="cell-value">${orderNumber}</td>
              </tr>
              <tr>
                <td class="cell-label">物流单号</td>
                <td class="cell-value">${trackingNumber}</td>
                <td class="cell-label">物流公司</td>
                <td class="cell-value">${logisticsCompany}</td>
              </tr>
            </table>

            <table class="print-table unit-info-table">
              <tr>
                <td colspan="2" class="unit-group-title demand-title">需求单位</td>
                <td colspan="2" class="unit-group-title supplier-title">供应商单位</td>
              </tr>
              <tr>
                <td class="cell-label">单位名称</td>
                <td class="cell-value">${demandUnitName}</td>
                <td class="cell-label">单位名称</td>
                <td class="cell-value">${supplierName}</td>
              </tr>
              <tr>
                <td class="cell-label">二级机构</td>
                <td class="cell-value">${secondaryOrg}</td>
                <td class="cell-label">联系人</td>
                <td class="cell-value">${supplierContactPerson}</td>
              </tr>
              <tr>
                <td class="cell-label">联系人</td>
                <td class="cell-value">${demandContactPerson}</td>
                <td class="cell-label">联系电话</td>
                <td class="cell-value">${supplierContactPhone}</td>
              </tr>
              <tr>
                <td class="cell-label">联系电话</td>
                <td class="cell-value">${demandContactPhone}</td>
                <td colspan="2" class="cell-spacer"></td>
              </tr>
              <tr>
                <td class="cell-label">收货地址</td>
                <td class="cell-value address-value" colspan="3">${deliveryAddress}</td>
              </tr>
            </table>

            <table class="print-table product-table">
              <thead>
                <tr>
                  <th>序号</th>
                  <th>产品名称</th>
                  <th>规格型号</th>
                  <th>材质要求</th>
                  <th>单位</th>
                  <th>发货数量</th>
                  <th>${priceColumnTitle}</th>
                  <th>发货日期</th>
                  <th>备注</th>
                </tr>
              </thead>
              <tbody>
                ${shipmentItems.length > 0 ? shipmentItems.map((item, index) => `
                  <tr>
                    <td class="text-center">${index + 1}</td>
                    <td>${item.productName || ''}</td>
                    <td>${item.specification || ''}</td>
                    <td>${item.materialRequirement || ''}</td>
                    <td class="text-center">${item.unit || ''}</td>
                    <td class="text-center">${item.shippedQuantity || ''}</td>
                    <td class="text-center">${getPriceValue(item)}</td>
                    <td class="text-center">${item.shipmentDate || ''}</td>
                    <td>${item.remark || ''}</td>
                  </tr>
                `).join('') : '<tr><td colspan="9" class="text-center">暂无发货产品信息</td></tr>'}
              </tbody>
            </table>

            <div class="signature-section">
              <div class="signature-item">
                <span class="signature-label">收货人签字确认：</span>
                ${signatureHtml}
              </div>
              <div class="signature-item signature-item-right">
                <span class="signature-label">发货单位经办人：</span>
                ${supplierSignatureHtml}
              </div>
            </div>

            <div class="footer-remark">备注：${logisticsInfo.remark || ''}</div>
          </div>
        `;
      
      // 创建打印窗口
      const printWindow = window.open('', '_blank', 'height=600,width=800');
      printWindow.document.write(`
        <html>
          <head>
            <title>送货单 - ${deliveryNoteNumber}</title>
            <style>
              * { box-sizing: border-box; }
              body {
                font-family: SimSun, "Songti SC", serif;
                padding: 72px 20px 20px;
                color: #222;
                background: #fff;
              }
              .delivery-note {
                max-width: 920px;
                margin: 0 auto;
              }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
              thead { display: table-header-group; }
              tfoot { display: table-footer-group; }

              .doc-header {
                text-align: center;
                margin-bottom: 18px;
                padding-bottom: 12px;
                border-bottom: 2px solid #333;
              }
              .doc-company {
                margin: 0 0 6px;
                font-size: 22px;
                font-weight: bold;
                letter-spacing: 1px;
              }
              .doc-title {
                margin: 0 0 10px;
                font-size: 18px;
                font-weight: bold;
                letter-spacing: 6px;
              }
              .doc-header-meta {
                display: grid;
                grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
                align-items: center;
                column-gap: 12px;
                min-height: 22px;
                font-size: 13px;
                color: #444;
              }
              .doc-ship-date {
                grid-column: 2;
                text-align: center;
                white-space: nowrap;
              }
              .doc-number {
                grid-column: 3;
                text-align: right;
                white-space: nowrap;
              }

              .print-table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
                margin-bottom: 16px;
                font-size: 13px;
              }
              .print-table th,
              .print-table td {
                border: 1px solid #333;
                padding: 8px 10px;
                line-height: 1.6;
                vertical-align: middle;
                word-break: break-all;
              }
              .cell-label {
                width: 80px;
                text-align: center;
                background: #f7f7f7;
                color: #333;
                font-weight: normal;
                white-space: nowrap;
              }
              .cell-value {
                text-align: left;
              }
              .cell-spacer {
                background: #fff;
              }

              .meta-table .cell-label {
                width: 88px;
              }

              .unit-group-title {
                text-align: center;
                font-weight: bold;
                font-size: 14px;
                padding: 9px 10px;
                background: #efefef;
              }
              .demand-title {
                border-right: 1px solid #666;
              }
              .address-value {
                min-height: 36px;
              }

              .product-table th {
                text-align: center;
                background: #efefef;
                font-weight: bold;
                padding: 9px 6px;
                font-size: 12px;
              }
              .product-table td {
                font-size: 12px;
                padding: 7px 6px;
              }
              .text-center {
                text-align: center;
              }

              .signature-section {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin: 24px 0 16px;
                min-height: 90px;
              }
              .signature-item {
                flex: 1;
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 13px;
              }
              .signature-item-right {
                justify-content: flex-end;
              }
              .signature-label {
                white-space: nowrap;
              }

              .footer-remark {
                margin-top: 8px;
                font-size: 12px;
                color: #555;
                line-height: 1.6;
              }

              .print-controls {
                position: fixed;
                top: 10px;
                right: 10px;
                background-color: #f0f0f0;
                padding: 10px;
                border-radius: 4px;
                box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
                z-index: 9999;
              }
              
              .print-controls button {
                margin-right: 10px;
                padding: 8px 15px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                color: white;
              }
              
              .print-controls button:last-child {
                margin-right: 0;
              }
              
              .print-controls .print-btn {
                background-color: #409eff;
              }
              
              .print-controls .close-btn {
                background-color: #909399;
              }
              
              .print-note {
                font-size: 12px;
                color: #606266;
                margin-top: 5px;
              }
              
              @media print {
                body {
                  padding-top: 20px;
                }
                .print-controls {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-controls">
              <button class="print-btn" onclick="doPrint()">打印</button>
              <button class="close-btn" onclick="window.close()">关闭窗口</button>
              <div class="print-note">打印完成后窗口将在60秒后自动关闭，或点击"关闭窗口"按钮手动关闭</div>
            </div>
            
            <div id="delivery-content"></div>
          </body>
        </html>
      `);
      
      await installPlatformPrintFooter(printWindow, null, {
        documentType: 'DELIVERY_NOTE',
        documentKey: deliveryNoteNumber
      });
      // 关闭文档流
      printWindow.document.close();
      
      // 在窗口加载完成后注入内容和处理打印
      printWindow.onload = function() {
        // 设置送货单内容
        printWindow.document.getElementById('delivery-content').innerHTML = deliveryNoteHTML;
        
        // 添加脚本
        const script = printWindow.document.createElement('script');
        script.textContent = `
          function doPrint() {
            window.print();
          }
          
          // 页面加载完成自动打印
          window.addEventListener('load', function() {
            window.focus();
            
            // 延迟打印，确保内容已加载
            setTimeout(function() {
              window.print();
            }, 500);
            
            // 60秒后自动关闭窗口
            setTimeout(function() {
              window.close();
            }, 60000);
          });
          
          // 尝试监听打印完成事件
          if (window.matchMedia) {
            const mediaQueryList = window.matchMedia('print');
            mediaQueryList.addEventListener('change', function(mql) {
              if (!mql.matches) {
                // 打印对话框已关闭，5秒后关闭窗口
                setTimeout(function() {
                  window.close();
                }, 5000);
              }
            });
          }
        `;
        printWindow.document.body.appendChild(script);
      };
      
      } catch (error) {
        console.error('获取发货清单失败:', error);
        this.$message.error('获取发货清单失败，无法打印送货单');
      }
    },
    
    // 计算总发货数量
    getTotalQuantity() {
      if (!this.currentShipmentDetail || !this.currentShipmentDetail.shipmentItems) {
        return '0';
      }
      const total = this.currentShipmentDetail.shipmentItems.reduce((sum, item) => {
        return sum + (parseFloat(item.shippedQuantity) || 0);
      }, 0);
      return this.formatQuantity(total);
    },
    
    // 计算总金额
    getTotalAmount() {
      if (!this.currentShipmentDetail || !this.currentShipmentDetail.shipmentItems) {
        return '0.00';
      }
      const total = this.currentShipmentDetail.shipmentItems.reduce((sum, item) => {
        return sum + (parseFloat(item.taxInclusiveTotalAmount) || 0);
      }, 0);
      return this.formatAmount(total);
    },
    
    // 格式化发货日期
    formatShipmentDate() {
      if (!this.currentShipmentDetail || !this.currentShipmentDetail.shipmentItems || this.currentShipmentDetail.shipmentItems.length === 0) {
        return '--';
      }
      
      // 获取第一个产品的发货日期
      const firstItem = this.currentShipmentDetail.shipmentItems[0];
      if (firstItem.shipmentDate) {
        return firstItem.shipmentDate;
      }
      
      // 如果没有发货日期，使用物流信息的创建时间
      if (this.currentShipmentDetail.createTime) {
        return this.currentShipmentDetail.createTime.split('T')[0];
      }
      
      return '--';
    },
    
    // 脱敏姓名
    maskName(name) {
      return maskNameUtil(name);
    },
    
    maskPhone(phone) {
      return phone || '';
    }
  }
}
</script>

<style lang="scss" scoped>
.order-shipment-container {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 120px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-title {
  display: flex;
  align-items: center;
}

.title-mark {
  width: 4px;
  height: 20px;
  background-color: #409EFF;
  margin-right: 8px;
}

.header-title span {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.info-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.selected-count {
  font-size: 12px;
  color: #409eff;
  font-weight: normal;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 14px;
  
  label {
    font-weight: 500;
    color: #606266;
    min-width: 120px;
    margin-right: 10px;
    font-size: 14px;
  }
  
  span {
    color: #333;
    flex: 1;
    font-size: 14px;
  }
}

.unit-info-block {
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
  
  .unit-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #409EFF;
  }
  
  &.demand-unit .unit-title {
    border-bottom-color: #67C23A;
  }
  
  &.supplier-unit .unit-title {
    border-bottom-color: #409EFF;
  }
  
  .info-item {
    margin-bottom: 12px;
    
    label {
      min-width: 100px;
    }
  }
}

// 订单状态颜色
.info-item .status-text {
  font-weight: 500;
}
.info-item .status-ACTIVE {
  color: #E6A23C !important; /* warning */
}
.info-item .status-EXECUTING {
  color: #67C23A !important; /* success */
}
.info-item .status-SENDING {
  color: #409EFF !important; /* primary */
}
.info-item .status-RECEIVING {
  color: #909399 !important; /* info */
}
.info-item .status-END, .info-item .status-COMPLETED {
  color: #67C23A !important; /* success */
}
.info-item .status-REJECTED, .info-item .status-CANCELLED {
  color: #F56C6C !important; /* danger */
}

// 订单类型颜色
.info-item .order-type-text {
  font-weight: 500;
}
.info-item .order-type-01 {
  color: #67C23A !important; /* 正常订单 - success */
}
.info-item .order-type-02 {
  color: #F56C6C !important; /* 退货订单 - danger */
}

.total-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #EBEEF5;
  text-align: right;
  font-weight: bold;
  
  span {
    color: #606266;
    font-size: 14px;
    
    &:first-child {
      color: #409EFF;
    }
    
    &:nth-child(2) {
      color: #E6A23C;
    }
  }
}

.dialog-content {
  .form-section {
    margin-bottom: 30px;
    
    .form-section-title {
      font-size: 16px;
      font-weight: 500;
      color: #333;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      align-items: center;
      justify-content: space-between;
      
      .selected-count {
        font-size: 12px;
        color: #409eff;
        font-weight: normal;
      }
    }
  }
}

.product-selection-area {
  .selection-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 4px;
    
    .selection-info {
      font-size: 12px;
      color: #606266;
    }
  }
}

.no-stock {
  color: #f56c6c;
  font-weight: bold;
}

.shipment-history-area {
  .received-text {
    color: #67c23a;
    font-weight: bold;
  }
  
  .pending-text {
    color: #e6a23c;
  }
}

.no-data {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.action-section {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  .el-button {
    margin: 0 10px;
    min-width: 120px;
  }
}

.dialog-footer {
  text-align: right;
}

.attachment-record-title {
  margin-bottom: 8px;
  color: #606266;
  font-size: 14px;
}

.receive-attachment-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

// 表格样式优化
::v-deep .el-table th {
  background-color: #f5f7fa;
}

::v-deep .el-table--border {
  border-radius: 4px;
  overflow: hidden;
}

// 表单项样式
.el-form-item {
  margin-bottom: 20px;
}

.el-input, .el-textarea {
  width: 100%;
}

// 发货产品清单对话框样式
.shipment-items-table {
  .summary-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    
    label {
      font-weight: 500;
      color: #606266;
      min-width: 80px;
      margin-right: 8px;
      font-size: 14px;
    }
    
    .highlight {
      color: #409EFF;
      font-weight: bold;
      font-size: 14px;
    }
    
    .total-amount {
      color: #E6A23C;
      font-size: 16px;
    }
  }
}

// 发货方式选择样式
.el-radio-group {
  .el-radio {
    margin-right: 30px;
    font-size: 14px;
    
    &:last-child {
      margin-right: 0;
    }
  }
}

</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>
